import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  DEFAULT_CHECK_INTERVAL_MS,
  IS_VERSION_CHECK_ENABLED,
  MIN_EVENT_CHECK_INTERVAL_MS,
  VERSION_URL,
} from '@/shared/hooks/use-app-version-refresh-prompt/use-app-version-refresh-prompt.constants';
import type {
  TUseAppVersionRefreshPrompt,
  TUseAppVersionRefreshPromptOptions,
} from '@/shared/hooks/use-app-version-refresh-prompt/use-app-version-refresh-prompt.types';
import {
  isAppVersion,
  isChunkLoadError,
  reportVersionFileProblem,
} from '@/shared/hooks/use-app-version-refresh-prompt/use-app-version-refresh-prompt.utils';

export const useAppVersionRefreshPrompt = ({
  checkIntervalMs = DEFAULT_CHECK_INTERVAL_MS,
  enabled = IS_VERSION_CHECK_ENABLED,
  versionUrl = VERSION_URL,
}: TUseAppVersionRefreshPromptOptions = {}): TUseAppVersionRefreshPrompt => {
  const [isUpdateAvailable, setIsUpdateAvailable] = useState(false);
  const isCheckingRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const deployedVersionUrl = useMemo(
    () => new URL(versionUrl, window.location.origin).toString(),
    [versionUrl],
  );

  const refreshApp = useCallback(() => {
    window.location.reload();
  }, []);

  const showRefreshPrompt = useCallback(() => {
    setIsUpdateAvailable(true);
  }, []);

  const checkVersion = useCallback(async () => {
    if (!enabled || isUpdateAvailable || isCheckingRef.current) {
      return;
    }

    isCheckingRef.current = true;

    try {
      const abortController =
        typeof AbortController === 'function' ? new AbortController() : null;

      abortControllerRef.current = abortController;

      const response = await fetch(deployedVersionUrl, {
        cache: 'no-store',
        ...(abortController ? { signal: abortController.signal } : {}),
      });

      if (!response.ok) {
        reportVersionFileProblem(
          deployedVersionUrl,
          `responded with ${response.status}`,
        );

        return;
      }

      // CloudFront rewrites 403/404 to a 200 /index.html, so a missing version
      // file arrives as an ok response with an HTML body.
      const data = await response.json().catch(() => {
        reportVersionFileProblem(deployedVersionUrl, 'did not return JSON');

        return null;
      });

      if (
        isAppVersion(data) &&
        typeof data.version === 'string' &&
        data.version !== __APP_VERSION__
      ) {
        showRefreshPrompt();
      }
    } catch {
      // Version checks are best-effort; normal app usage must not be interrupted.
    } finally {
      abortControllerRef.current = null;
      isCheckingRef.current = false;
    }
  }, [deployedVersionUrl, enabled, isUpdateAvailable, showRefreshPrompt]);

  useEffect(() => () => abortControllerRef.current?.abort(), []);

  useEffect(() => {
    if (!enabled || isUpdateAvailable) {
      return;
    }

    let lastEventCheckAt = 0;

    const checkOnReturnToTab = () => {
      const now = Date.now();

      if (
        document.hidden ||
        now - lastEventCheckAt < MIN_EVENT_CHECK_INTERVAL_MS
      ) {
        return;
      }

      lastEventCheckAt = now;
      void checkVersion();
    };

    const interval = window.setInterval(() => {
      void checkVersion();
    }, checkIntervalMs);

    void checkVersion();
    window.addEventListener('focus', checkOnReturnToTab);
    document.addEventListener('visibilitychange', checkOnReturnToTab);

    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', checkOnReturnToTab);
      document.removeEventListener('visibilitychange', checkOnReturnToTab);
    };
  }, [checkIntervalMs, checkVersion, enabled, isUpdateAvailable]);

  useEffect(() => {
    if (!enabled || isUpdateAvailable) {
      return;
    }

    const handleError = (event: ErrorEvent) => {
      if (isChunkLoadError(event.error ?? event.message)) {
        showRefreshPrompt();
      }
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (isChunkLoadError(event.reason)) {
        showRefreshPrompt();
      }
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener(
        'unhandledrejection',
        handleUnhandledRejection,
      );
    };
  }, [enabled, isUpdateAvailable, showRefreshPrompt]);

  return {
    isUpdateAvailable,
    refreshApp,
  };
};
